import { CartUpdatedEvent } from '@theme/events';

class CartDiscount extends HTMLElement {
  constructor() {
    super();

    this.applyButton = null;
    this.inputDiscount = null;
    this.removeDiscountButtons = [];

    this.handleApply = this.handleApply.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
  }

  connectedCallback() {
    this.applyButton = this.querySelector('[data-ref="apply-discount"]');
    this.inputDiscount = this.querySelector('[data-ref="discount-input"]');
    this.removeDiscountButtons = [
      ...this.querySelectorAll('[data-ref="remove-discount"]')
    ];

    this.applyButton?.addEventListener('click', this.handleApply);
    this.inputDiscount?.addEventListener('keydown', this.handleKeydown);

    this.removeDiscountButtons.forEach((button) => {
      button.addEventListener('click', this.handleRemove);
    });
  }

  disconnectedCallback() {
    this.applyButton?.removeEventListener('click', this.handleApply);
    this.inputDiscount?.removeEventListener('keydown', this.handleKeydown);

    this.removeDiscountButtons.forEach((button) => {
      button.removeEventListener('click', this.handleRemove);
    });
  }

  handleApply(event) {
    event.preventDefault();
    event.stopPropagation();

    this.submitDiscount();
  }

  handleKeydown(event) {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    event.stopPropagation();

    this.submitDiscount();
  }

  async submitDiscount() {
    const discountCode = this.inputDiscount?.value?.trim();

    if (!discountCode) {
      this.setErrorMode(
        this.dataset.pleaseInsertACode || 'Veuillez saisir un code promo.'
      );
      return;
    }

    const existingDiscounts = this.getExistingDiscounts();

    const alreadyApplied = existingDiscounts.some(
      (code) => code.toLowerCase() === discountCode.toLowerCase()
    );

    if (alreadyApplied) {
      this.setErrorMode(
        this.dataset.codeAlreadyApplied || 'Ce code est déjà appliqué.'
      );
      return;
    }

    this.setButtonLoading();

    try {
      const codes = [...existingDiscounts, discountCode];

      const data = await this.updateDiscounts(codes);

      /*
       * Shopify retourne discount_codes dans le panier.
       * On regarde si notre code a été refusé.
       */
      const failedDiscount = data.discount_codes?.find((discount) => {
        return (
          discount.code?.toLowerCase() === discountCode.toLowerCase() &&
          discount.applicable === false
        );
      });

      if (failedDiscount) {
        this.setErrorMode(
          this.dataset.codeNotApplicable ||
            'Ce code promo ne peut pas être appliqué à ce panier.'
        );
        return;
      }

      /*
       * Si Shopify ne retourne aucune section,
       * on recharge simplement la page.
       */
      if (!data.sections) {
        window.location.reload();
        return;
      }

      this.updateCart(data);

    } catch (error) {
      console.error('ERREUR CODE PROMO SHOPIFY :', error);

      /*
       * IMPORTANT :
       * on affiche maintenant l'erreur réelle,
       * au lieu du message générique.
       */
      this.setErrorMode(
        error?.message ||
          this.dataset.errorApplyDiscount ||
          "Erreur lors de l'application du code de réduction"
      );
    }
  }

  async handleRemove(event) {
    event.preventDefault();
    event.stopPropagation();

    const badge = event.currentTarget.closest(
      '[data-ref="discount-badge"]'
    );

    if (!badge) return;

    const discountCode = badge.dataset.discountCode;

    if (!discountCode) return;

    const existingDiscounts = this.getExistingDiscounts();

    const remainingDiscounts = existingDiscounts.filter(
      (code) => code.toLowerCase() !== discountCode.toLowerCase()
    );

    this.setButtonLoading();

    try {
      const data = await this.updateDiscounts(remainingDiscounts);

      if (!data.sections) {
        window.location.reload();
        return;
      }

      this.updateCart(data);

    } catch (error) {
      console.error(
        'ERREUR SUPPRESSION CODE PROMO SHOPIFY :',
        error
      );

      this.setErrorMode(
        error?.message ||
          this.dataset.errorRemoveDiscount ||
          'Impossible de supprimer le code promo.'
      );
    }
  }

  async updateDiscounts(discountCodes) {
    /*
     * IMPORTANT :
     * on n'utilise PAS Theme.routes.cart_update_url.
     *
     * Shopify recommande une URL locale :
     * window.Shopify.routes.root + 'cart/update.js'
     */
    const root =
      window.Shopify?.routes?.root ||
      '/';

    const url = `${root}cart/update.js`;

    const cartDrawer =
      document.querySelector('cart-drawer');

    const sectionId =
      cartDrawer?.dataset?.sectionId;

    const body = {
      discount: discountCodes.join(',')
    };

    /*
     * On demande directement à Shopify
     * de rerendre le cart drawer dans
     * la même requête.
     */
    if (sectionId) {
      body.sections = sectionId;

      body.sections_url =
        window.location.pathname +
        window.location.search;
    }

    console.log('Application code promo :', {
      url,
      body
    });

    const response = await fetch(url, {
      method: 'POST',

      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },

      credentials: 'same-origin',

      body: JSON.stringify(body)
    });

    /*
     * On récupère d'abord le texte brut.
     * Comme ça, si Shopify renvoie autre chose
     * que du JSON, on sait précisément pourquoi.
     */
    const responseText = await response.text();

    let data;

    try {
      data = JSON.parse(responseText);
    } catch (error) {
      console.error(
        'Réponse Shopify non JSON :',
        responseText
      );

      throw new Error(
        `Shopify a retourné une réponse invalide (${response.status})`
      );
    }

    if (!response.ok) {
      console.error(
        'Erreur Shopify :',
        data
      );

      throw new Error(
        data.description ||
        data.message ||
        `Erreur Shopify ${response.status}`
      );
    }

    console.log(
      'Réponse code promo Shopify :',
      data
    );

    return data;
  }

  updateCart(data) {
    /*
     * Sur la page /cart :
     * rechargement classique.
     */
    if (window.location.pathname.includes('/cart')) {
      window.location.reload();
      return;
    }

    /*
     * Dans le cart drawer :
     * on utilise EXACTEMENT le système natif
     * déjà prévu dans ton thème.
     */
    document.dispatchEvent(
      new CartUpdatedEvent(
        data,
        {
          itemCount: data.item_count,
          source: 'cart-discount',
          sections: data.sections
        }
      )
    );

    this.setButtonLoaded();
  }

  getExistingDiscounts() {
    const discountCodes = [];

    const discountBadges =
      this.querySelectorAll(
        '[data-ref="discount-badge"]'
      );

    discountBadges.forEach((badge) => {
      const code =
        badge.dataset.discountCode;

      if (code) {
        discountCodes.push(
          code.trim()
        );
      }
    });

    return [...new Set(discountCodes)];
  }

  setErrorMode(message) {
    this.setButtonLoaded();

    document.dispatchEvent(
      new CustomEvent('toast:open', {
        detail: {
          type: 'error',
          message
        }
      })
    );

    const textfield =
      this.inputDiscount?.closest('.textfield');

    textfield?.classList.add(
      'textfield--error'
    );

    if (this.inputDiscount) {
      this.inputDiscount.value = '';
    }

    setTimeout(() => {
      textfield?.classList.remove(
        'textfield--error'
      );
    }, 2500);
  }

  setButtonLoading() {
    if (!this.applyButton) return;

    this.applyButton.disabled = true;

    this.applyButton.classList.add(
      'is-loading'
    );
  }

  setButtonLoaded() {
    if (!this.applyButton) return;

    this.applyButton.disabled = false;

    this.applyButton.classList.remove(
      'is-loading'
    );
  }
}

if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    CartDiscount
  );
}